<template>
    <v-card>
        <CodeReaderDialog
            :is-open="isQrOpen"
            :settings="qrSettings"
            parse-as="text"
            @code-stream-stop="isQrOpen = false"
            @code-stream-handle="onQrScanned"
        />

        <v-toolbar flat dense color="white lighten-2">
            <v-toolbar-title>Palety</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text>
            <v-row>
                <v-col cols="7">
                    <v-text-field
                        ref="palletNumberField"
                        v-model="pallet"
                        clearable
                        label="Numer palety"
                        hint="Wpisz lub zeskanuj numer palety"
                        outlined
                        dense
                        :error-messages="palletErrors"
                        :disabled="isCheckingPallet"
                        autofocus
                        @input="$v.pallet.$touch()"
                    />
                </v-col>
                <v-col v-if="hasCamera" cols="auto" class="px-0 mt-1">
                    <v-btn
                        fab
                        x-small
                        color="primary"
                        class="elevation-1 mr-3 ml-0"
                        data-sentry-label="Otwórz czytnik kodów"
                        @click="openQrScanner"
                    >
                        <v-icon>mdi-camera</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="auto" class="px-0">
                    <v-btn
                        color="success"
                        data-sentry-label="Dodaj numer palety"
                        :loading="isCheckingPallet"
                        :disabled="$v.pallet.$invalid || isCheckingPallet"
                        @click="addPalletToList(pallet)"
                    >
                        <span> Dodaj </span>
                    </v-btn>
                </v-col>
            </v-row>
            <v-data-table
                :headers="headers"
                :items="allPallets"
                :mobile-breakpoint="0"
                hide-default-footer
            >
                <template #item.parameter="{item}">
                    {{ item.parameter }}
                </template>
                <template #item.details="{item}">
                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn icon data-sentry-label="Usuń paletę" v-on="on" @click="removePallet(item)">
                                <v-icon color="pink">
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Usuń</span>
                    </v-tooltip>
                </template>
                <template slot="no-results">
                    <p class="text-center">
                        Brak danych
                    </p>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { required } from 'vuelidate/lib/validators'
import CodeReaderDialog from '@/components/common/CodeReaderDialog.vue'

export default {
    name: 'PaperPalletReport',
    components: {
        CodeReaderDialog
    },
    mixins: [
        pagination,
        dateFormatter,
        tooltipDelay,
    ],
    props: {
        palletsList: {
            type: Array,
            default: () => []
        },
        reportId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            allPallets: [],
            headers: [
                {
                    text: 'Numer palety',
                    value: 'parameter',
                    sortable: false,
                    width: 300,
                },
                {
                    text: 'Akcje',
                    value: 'details',
                    sortable: false,
                    align: 'center'
                }
            ],
            pallet: null,
            isQrOpen: false,
            isCheckingPallet: false,
            qrSettings: {
                origin: 'paperPallet',
                expectedQrTypeCodes: []
            }
        }
    },
    validations() {
        return {
            pallet: {
                required,
                isCorrectPallet: value => {
                    return /\.CF$/.test(value)
                }
            }
        }
    },
    computed: {
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
        lastPallet() {
            return this.allPallets.length === 1
        },
        palletErrors() {
            const errors = []

            if (!this.$v.pallet.$dirty) return errors
            !this.$v.pallet.required && errors.push('Kod palety jest wymagany.')
            !this.$v.pallet.isCorrectPallet && errors.push('Podany kod jest nieprawidłowy')

            return errors
        },
    },
    watch: {
        palletsList(newValue) {
            this.allPallets = newValue
        },
    },
    methods: {
        openQrScanner() {
            this.isQrOpen = true
        },
        onQrScanned({ data }) {
            this.pallet = data.trim()
            this.$v.pallet.$touch()

            if (!this.$v.pallet.$invalid) {
                this.addPalletToList(this.pallet)
            } else {
                this.setFocus()
            }

            this.isQrOpen = false
        },
        removePallet(item) {
            const index = this.palletsList.indexOf(item)

            if (this.reportId) {
                this.$store.dispatch('reports/removePallet', item.id)
                    .then(() => {
                        this.allPallets.splice(index, 1)
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            } else {
                this.allPallets.splice(index, 1)
            }

            this.changedPalletList()
        },
        async addPalletToList(palletNumber) {
            const trimmedPallet = typeof palletNumber === 'string'
                ? palletNumber.trim()
                : ''

            this.pallet = trimmedPallet
            this.$v.pallet.$touch()

            if (!trimmedPallet || this.$v.pallet.$invalid) {
                this.setFocus()

                return
            }

            const alreadyExistedPallet = this.allPallets.find(pallet => pallet.parameter === trimmedPallet)

            if (alreadyExistedPallet) {
                this.$store.dispatch('showNotification', {
                    type: 'primary',
                    message: 'Wprowadzony numer został już wcześniej dodany.'
                })
                this.pallet = null
                this.setFocus()

                return
            }

            this.isCheckingPallet = true

            let parameterToSave = trimmedPallet

            try {
                const validationResult = await this.$store.dispatch('reports/checkPalletParameter', trimmedPallet)

                if (validationResult && validationResult.exists === false) {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Paleta o podanym numerze nie istnieje.'
                    })
                    this.pallet = null
                    this.isCheckingPallet = false
                    this.setFocus()

                    return
                }

                if (validationResult && typeof validationResult.parameter === 'string') {
                    parameterToSave = validationResult.parameter.trim()
                }
            } catch (error) {
                this.isCheckingPallet = false
                this.setFocus()

                return
            }

            if (this.allPallets.find(pallet => pallet.parameter === parameterToSave)) {
                this.$store.dispatch('showNotification', {
                    type: 'primary',
                    message: 'Wprowadzony numer został już wcześniej dodany.'
                })
                this.pallet = null
                this.isCheckingPallet = false
                this.setFocus()

                return
            }

            if (!this.reportId) {
                this.allPallets.push({ 'parameter': parameterToSave })
                this.pallet = null
                this.isCheckingPallet = false
                this.changedPalletList()

                return
            }

            this.$store.dispatch('reports/addPallet', {
                'reportId': this.reportId,
                'parameter': parameterToSave
            })
                .then(response => {
                    this.allPallets.push({ 'parameter': response.parameter, 'reportId': response.reportId, 'id': response.id })
                    this.pallet = null
                    this.isCheckingPallet = false
                    this.changedPalletList()
                })
                .catch(error => {
                    errorHandler(error)
                    this.isCheckingPallet = false
                    this.setFocus()
                })
        },
        setFocus() {
            this.$v.pallet.$reset()
            this.$refs.palletNumberField.focus()
        },
        changedPalletList() {
            this.setFocus()
            this.$emit('on-change', this.allPallets)
        }
    }
}
</script>
